import axios, { type InternalAxiosRequestConfig } from "axios";
import { getBridgeAccessToken } from "auth/accessTokenBridge";

const origin = (process.env.REACT_APP_BASE_URL ?? "").replace(/\/$/, "");

export type ApiVersion = "v1" | "v2";

/** Base URL del API para la versión indicada. */
export function getApiBaseUrl(version: ApiVersion = "v1"): string {
  return `${origin}/api/${version}`;
}

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";

const sharedClientConfig = {
  withCredentials: true,
  xsrfCookieName: "csrftoken" as const,
  xsrfHeaderName: "X-CSRFToken" as const,
};

function attachAuthInterceptor(instance: ReturnType<typeof axios.create>): void {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getBridgeAccessToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  });
}

function createApiClient(version: ApiVersion) {
  const client = axios.create({
    baseURL: getApiBaseUrl(version),
    ...sharedClientConfig,
  });
  attachAuthInterceptor(client);
  return client;
}

/** Cliente por defecto: API v1. */
export const apiClient = createApiClient("v1");

/** Cliente para rutas bajo `/api/v2/`. */
export const apiClientV2 = createApiClient("v2");

export function getApiClient(version: ApiVersion = "v1") {
  return version === "v2" ? apiClientV2 : apiClient;
}

/**
 * Con el front en otro origen, la cookie csrftoken del API no es legible desde JS.
 * Se aplica el token a v1 y v2 si comparten sesión Django.
 */
export async function prefetchApiCsrfToken(): Promise<void> {
  const { data } = await apiClient.get<{ csrfToken: string }>("csrf/");
  const token = data?.csrfToken;
  if (token) {
    apiClient.defaults.headers.common["X-CSRFToken"] = token;
    apiClientV2.defaults.headers.common["X-CSRFToken"] = token;
  }
}

export default apiClient;
